import { PropsWithChildren, useCallback, useRef } from 'react';
import { Color } from 'lib/Button';
import { useOutsideClick } from 'customHooks/useOutsideClick';
import warningSvg from 'assets/icons/warning.svg';
import Alert from 'lib/Alert';
import { AlertThemeOption } from 'lib/Alert/Alert.types';
import useNotification from 'components/pages/Planner/PlannerSections/Objectives/useNotification';
import usePendoTracking from 'customHooks/usePendoTracking';
import { PanelSize, SecondaryPanelProps } from './SecondaryPanel.types';
import { SecondaryPanelHeader } from './SecondaryPanelHeader';
import { SecondaryPanelActions } from './SecondaryPanelActions';

const PANEL_SIZE_HEIGHT: Partial<Record<PanelSize, string>> = {
  md: '50vh',
  lg: '75vh',
};

const getPanelStyles = (customStyles: SecondaryPanelProps['customStyles'], size: PanelSize): React.CSSProperties => {
  const { bottom: customBottom, ...restCustomStyles } = customStyles ?? {};

  return {
    top: restCustomStyles?.top ?? '60px',
    left: restCustomStyles?.left ?? '460px',
    ...(size === 'full' ? { bottom: customBottom ?? '16px' } : { height: PANEL_SIZE_HEIGHT[size] }),
    ...restCustomStyles,
  };
};

export const SecondaryPanel = ({
  children,
  customStyles,
  size = 'full',
  title = '',
  isDisabled = false,
  showName = true,
  name = '',
  showBackButton = false,
  cancelButtonLabel = 'Cancel',
  confirmButtonLabel = 'Save',
  confirmButtonColor = Color.PRIMARY,
  onSubmit,
  onCancel,
  isSubsection = false,
  isMainLevel = true,
  isChange = false,
  isOutsideClickDisabled = false,
  pendoId,
}: PropsWithChildren<SecondaryPanelProps>): React.ReactElement => {
  const panelRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const { getPendoDataId } = usePendoTracking();

  const {
    closeNotification,
    handleBackNavigation,
    handleDiscard,
    isOpen: isAlertOpen,
  } = useNotification(isSubsection && isChange);

  const handleCancel = useCallback((): void => {
    if (isSubsection && isMainLevel) handleBackNavigation();
    else onCancel();
  }, [onCancel, handleBackNavigation, isSubsection, isMainLevel]);

  useOutsideClick(panelRef, handleCancel, !isOutsideClickDisabled);

  const panelStyles = getPanelStyles(customStyles, size);

  return (
    <div
      data-test-id="planner-secondary-panel"
      className="fixed !my-0 flex flex-col justify-between items-center rounded-md border border-gray-300 bg-neutral-50 z-50 w-[var(--secondary-panel-width)]"
      style={panelStyles}
      id="secondary-panel-container"
      ref={panelRef}
    >
      <SecondaryPanelHeader
        title={title}
        isBackButtonShown={showBackButton}
        isNameShown={showName}
        name={name}
        onCancel={handleCancel}
      />
      <div data-test-id="planner-secondary-panel-content" className="flex-1 min-h-0 w-full py-3 px-3 overflow-y-auto">
        {isAlertOpen && onSubmit ? (
          <div className="my-2">
            <Alert
              onSave={onSubmit}
              onCancel={handleDiscard}
              onClose={closeNotification}
              icon={warningSvg}
              theme={AlertThemeOption.YELLOW}
              header="Save selection?"
              description="Going back without saving will clear your selection."
            />
          </div>
        ) : null}
        {children}
      </div>
      {onSubmit && (
        <SecondaryPanelActions
          cancelButtonLabel={cancelButtonLabel}
          confirmButtonLabel={confirmButtonLabel}
          confirmButtonColor={confirmButtonColor}
          isDisabled={isDisabled}
          onCancel={handleCancel}
          onSubmit={onSubmit}
          pendoId={pendoId}
          getPendoDataId={getPendoDataId}
        />
      )}
    </div>
  );
};

export default SecondaryPanel;
